import React, { useEffect, useState } from "react";
import cx from "./cx";
import RevAlert from "./RevAlert";
import RevBarra from "./RevBarra";
import RevButton from "./RevButton";
import RevCard from "./RevCard";
import RevChip from "./RevChip";
import RevDanger from "./RevDanger";
import RevField from "./RevField";
import RevKvRow from "./RevKvRow";
import RevPageHead from "./RevPageHead";
import RevSegmented from "./RevSegmented";
import RevTipRow from "./RevTipRow";

/**
 * Barra de esqueleto de carga (`.rev-skel`). El alto y el radio los pone `kind`
 * ('line' | 'sub' | 'figure' | 'title' | 'btn' | 'btn-sm' | 'chip' | 'texto' | 'cifra');
 * el ancho, la composición que la coloca (nunca un `style` suelto).
 *  - dark: sobre registro oscuro
 *  - count: repite la barra n veces
 *
 * 'texto' y 'cifra' son inline-block de `1em`: sustituyen a un texto DENTRO de una caja
 * real (una cifra en un `RevFigure`, el valor de un `RevKvRow`, la meta de un
 * `RevPageHead`), de modo que la caja de línea la sigue poniendo el contenedor y el alto
 * coincide con el del texto real en cualquier tamaño y en cualquier idioma.
 */
const RevSkeleton = ({ kind, dark, count = 1, className, ...rest }) => (
    <>
        {Array.from({ length: count }, (_, i) => (
            <span
                key={i}
                className={cx("rev-skel", kind && `rev-skel--${kind}`, dark && "rev-skel--dark", className)}
                {...rest}
                aria-hidden="true"
            />
        ))}
    </>
);

/**
 * La live region nace vacía y recibe `label` un frame después: un `role="status"` que ya
 * viene con texto no se anuncia al montarse.
 */
const useRotulo = (label) => {
    const [rotulo, setRotulo] = useState("");
    useEffect(() => {
        const id = requestAnimationFrame(() => setRotulo(label || ""));
        return () => cancelAnimationFrame(id);
    }, [label]);
    return rotulo;
};

/**
 * Molde: el nodo real con su cadena traducida dentro, que ocupa caja pero no se pinta ni
 * se lee. Sirve para todo texto que la pantalla escribe SIN depender de datos (título de
 * un aviso, párrafo de una card, ayuda, rótulo de un botón): el punto en el que envuelve
 * lo calcula el navegador con las métricas del texto de cada locale, no una constante.
 */
const Molde = ({ as: As = "span", className, children }) => (
    <As className={cx(className, "rev-skel", "rev-skel--molde")} aria-hidden="true">
        <span>{children}</span>
    </As>
);

/** Rótulo de botón como molde (barra inferior, pie de card, zona de peligro). */
const BotonMolde = ({ children, fit }) => (
    <RevButton as="span" fit={fit} className="rev-skel rev-skel--molde" aria-hidden="true">
        <span>{children}</span>
    </RevButton>
);

/** Vista de imagen: conserva el encuadre, los textos y las acciones de la pantalla. */
const Imagen = ({ titulo, ayuda, controles = [], acciones = [], clases = {} }) => (
    <div className={clases.contenedor}>
        <div className={clases.cabecera}>
            <Molde as="h2" className="rev-page-title">{titulo}</Molde>
            <Molde className="rev-help">{ayuda}</Molde>
        </div>
        <RevSkeleton dark className={clases.preview} />
        <div className={clases.controles}>
            {controles.map((icono, i) => (
                <RevButton key={i} as="span" icon className="rev-skel rev-skel--molde" aria-hidden="true">
                    <span className="material-icons rev-icon-sm">{icono}</span>
                </RevButton>
            ))}
        </div>
        <div className={clases.acciones}>
            {acciones.map((rotulo, i) => <BotonMolde key={i}>{rotulo}</BotonMolde>)}
        </div>
    </div>
);

/**
 * Celda de una fila. 'texto' envuelve la barra en un `<span>` para que la caja de línea la
 * ponga el strut real de la fila; el alto de la fila, en cambio, lo fija siempre la clase
 * real de la pantalla (su `min-height`), nunca el contenido del esqueleto.
 */
const Celda = ({ tipo }) => {
    if (tipo === "stack") {
        return (
            <div className="rev-skel-tabla__stack">
                <RevSkeleton kind="sub" />
                <RevSkeleton kind="line" />
            </div>
        );
    }
    if (tipo === "texto") return <span><RevSkeleton kind="texto" /></span>;
    if (!tipo) return <span />;
    return <RevSkeleton kind={tipo} />;
};

const filasDeCeldas = (filas, celdas, claseFila) => Array.from({ length: filas }, (_, i) => (
    <div key={i} className={cx(claseFila, "rev-skel-tabla__fila")}>
        {celdas.map((tipo, j) => <Celda key={j} tipo={tipo} />)}
    </div>
));

/**
 * Tabla con superficie propia: cabecera de alto fijo (vacía, como la real sin texto) y
 * `filas` filas de `celdas`. Cada entrada de `celdas` es 'texto' | 'sub' | 'line' |
 * 'chip' | 'btn-sm' | 'stack' (dos líneas de texto) | null (celda sin barra).
 * Con `cabeceraTexto`, la cabecera lleva una barra por columna dentro de un `<span>`,
 * para las cabeceras cuyo alto lo pone el strut del texto y no una medida propia.
 */
const Tabla = ({ filas = 10, celdas = [], clases = {}, cabeceraTexto }) => (
    <div className={clases.superficie}>
        <div className={clases.cabecera}>
            {cabeceraTexto && celdas.map((_, j) => <span key={j}><RevSkeleton kind="texto" /></span>)}
        </div>
        {filasDeCeldas(filas, celdas, clases.fila)}
    </div>
);

/**
 * Lienzo de barras por día: una columna por barra, con el valor arriba, el rectángulo en
 * medio y el día abajo, en las clases reales de la pantalla (`clases.lienzo` / `.barra` /
 * `.valor` / `.dia`). Todas las barras miden lo mismo —el hueco que el lienzo real deja—:
 * el esqueleto no insinúa qué día tuvo más consumo.
 */
const Barras = ({ barras = 7, clases = {} }) => (
    <div className={clases.lienzo}>
        {Array.from({ length: barras }, (_, i) => (
            <div key={i} className={clases.barra}>
                <span className={clases.valor}><RevSkeleton kind="texto" /></span>
                <span className="rev-skel rev-skel-barras__rect" aria-hidden="true" />
                <span className={clases.dia}><RevSkeleton kind="texto" /></span>
            </div>
        ))}
    </div>
);

/**
 * Lista de filas sin superficie (los pedidos recientes de Estadísticas, los cupones del
 * evento). Con `cabecera`, la fila de rótulos lleva las barras dentro de un `<span>` para
 * que el alto lo ponga el strut real de la cabecera, que no tiene alto propio.
 */
const Filas = ({ filas = 5, celdas = [], clases = {}, cabecera }) => (
    <div className={clases.lista}>
        {cabecera && (
            <div className={cx(clases.cabecera, "rev-skel-tabla__fila")}>
                {celdas.map((_, j) => <span key={j}><RevSkeleton kind="texto" /></span>)}
            </div>
        )}
        {filasDeCeldas(filas, celdas, clases.fila)}
    </div>
);

/** Lienzo oscuro de miniaturas con su cabecera y su pie de paginación (artboard L128-146). */
const Canvas = ({ miniaturas = 12 }) => (
    <div className="rev-photocanvas" data-register="dark">
        <div className="rev-photocanvas__head rev-skel-detalle__cabeza">
            <span className="rev-mono"><RevSkeleton kind="texto" dark /></span>
        </div>
        <div className="rev-photocanvas__grid">
            {Array.from({ length: miniaturas }, (_, i) => (
                <div key={i} className="rev-photocanvas__cell">
                    <span className="rev-tile rev-skel rev-skel--dark" />
                    {/* Tres líneas, no dos: el pie cargado es nombre (una línea, truncada)
                        más «fotógrafo · fecha», que a los anchos de celda habituales
                        (108-116 px) envuelve en dos. Medido en el banco: 43,5 px el
                        cargado, 44,5 el esqueleto. */}
                    <div className="rev-tile__meta">
                        <span className="rev-tile__meta-name"><RevSkeleton kind="texto" dark /></span>
                        <span><RevSkeleton kind="texto" dark /></span>
                        <span><RevSkeleton kind="texto" dark /></span>
                    </div>
                </div>
            ))}
        </div>
        <div className="rev-photocanvas__foot rev-skel-detalle__pie">
            <span className="rev-mono"><RevSkeleton kind="texto" dark /></span>
            <div className="rev-photocanvas__pagination">
                <RevSkeleton kind="btn-sm" dark />
                <RevSkeleton kind="btn-sm" dark />
                <RevSkeleton kind="btn" dark />
                <RevSkeleton kind="btn-sm" dark />
                <RevSkeleton kind="btn-sm" dark />
            </div>
        </div>
    </div>
);

const CardForm = () => (
    <div className="rev-card rev-skel-form__card">
        <RevSkeleton kind="line" className="rev-skel-form__titulo" />
        <div className="rev-form-grid">
            {[0, 1].map((i) => (
                <div key={i} className="rev-skel-form__campo">
                    <RevSkeleton kind="line" />
                    <RevSkeleton kind="btn" />
                    <RevSkeleton kind="line" />
                </div>
            ))}
        </div>
    </div>
);

const CardTips = () => (
    <div className="rev-card rev-skel-form__card rev-skel-form__card--tips">
        <RevSkeleton kind="line" className="rev-skel-form__titulo" />
        {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rev-skel-form__tip">
                <span className="rev-skel rev-skel-form__punto" aria-hidden="true" />
                <div className="rev-skel-form__tip-t">
                    <RevSkeleton kind="line" />
                    <RevSkeleton kind="line" />
                </div>
            </div>
        ))}
    </div>
);

/**
 * Columna de apoyo. Se pinta también con `aside === 0` cuando la pantalla real monta
 * siempre su `.rev-cols__aside` (vacío hasta que hay selección): así el ancho de la
 * columna principal es el mismo en los dos estados.
 */
const ColumnaAside = ({ aside = 0, className }) => (
    <div className={className || "rev-cols__aside"}>
        {Array.from({ length: aside }, (_, i) => <CardTips key={i} />)}
    </div>
);

/** Composición «form» del artboard (L158-198): `main` cards y `aside` cards de consejos. */
const Cards = ({ main = 2, aside = 1, clases = {} }) => {
    return (
        <div className={clases.cols || "rev-cols rev-cols--flush"}>
            <div className={clases.main || "rev-cols__main"}>
                {Array.from({ length: main }, (_, i) => <CardForm key={i} />)}
            </div>
            {aside > 0 && <ColumnaAside aside={aside} className={clases.aside} />}
        </div>
    );
};

/**
 * Campo de un formulario: el `RevField` real con una barra en la etiqueta, una barra de
 * alto de control en el hueco del control y, si el campo cargado la lleva, la MISMA ayuda
 * traducida como molde (los textos de ayuda envuelven, y un molde envuelve donde envuelve
 * el real en cada locale y a cada ancho; una barra de una línea dejaría el esqueleto corto).
 * `control` = 'switch' cuando el control real es un interruptor sin caja de control.
 */
const CampoMolde = ({ help, control }) => (
    <RevField
        className="rev-skel-form__campo-molde"
        label={<RevSkeleton kind="texto" />}
        help={help ? <Molde>{help}</Molde> : undefined}>
        <RevSkeleton kind={control === "switch" ? "switch" : "btn"} />
    </RevField>
);

const campoProps = (campo) => (typeof campo === "string" ? { help: campo } : campo || {});

/**
 * Aviso en línea: la superficie, el tono y el icono son los reales (quitarlos movería el
 * punto de envolvimiento del cuerpo); el título y el texto son moldes. El cuerpo lleva la
 * variante `molde-inline` porque el real es un `display: inline` dentro de un padre con
 * otro strut.
 */
const AlertaMolde = ({ tono = "info", titulo, texto }) => (
    <RevAlert tone={tono} title={<Molde>{titulo}</Molde>}>
        <Molde className="rev-help rev-skel--molde-inline">{texto}</Molde>
    </RevAlert>
);

/** Zona de peligro: el botón destructivo es un `<span>`, nunca una parada de tabulador. */
const DangerMolde = ({ titulo, texto, accion }) => (
    <RevDanger
        title={<Molde>{titulo}</Molde>}
        text={<Molde>{texto}</Molde>}
        action={<BotonMolde>{accion}</BotonMolde>} />
);

/**
 * Card de un formulario, compuesta con los componentes reales. Cada clave añade su pieza
 * en el orden en el que las pintan las pantallas. Todo lo que el estado cargado escribe
 * sin depender de datos llega como CADENA y se pinta de molde; solo los datos son barras.
 *  - sub: subtítulo
 *  - chip: rótulo del chip de estado
 *  - alerta: { tono, titulo, texto }
 *  - textos: párrafos de cuerpo (`.rev-card__text`)
 *  - campos: una rejilla por sub-array; cada campo es la cadena de su ayuda, un objeto
 *    { help, control } o un valor falso (campo sin ayuda)
 *  - divisor: filete sobre la segunda rejilla y siguientes
 *  - kv: claves de las filas clave/valor (el valor es dato: barra)
 *  - tips: [{ n, titulo, detalle }]
 *  - ayudas: cadenas (o { texto, divisor }) de las ayudas al pie de la card
 *  - botones: rótulos de los botones dentro de la card
 */
const CardMolde = ({ sub, chip, alerta, textos, campos, divisor, kv, tips, ayudas, botones }) => (
    <RevCard
        className="rev-skel-form__card"
        title={<RevSkeleton kind="texto" className="rev-skel-form__titulo" />}
        sub={sub ? <Molde>{sub}</Molde> : undefined}>
        {chip && <div><RevChip className="rev-skel rev-skel--molde" aria-hidden="true">{chip}</RevChip></div>}
        {alerta && <AlertaMolde {...alerta} />}
        {textos?.map((cadena, i) => <Molde key={i} as="p" className="rev-card__text">{cadena}</Molde>)}
        {campos?.map((rejilla, i) => (
            <div key={i} className={cx("rev-form-grid", divisor && i > 0 && "rev-form-grid--divider")}>
                {rejilla.map((campo, j) => <CampoMolde key={j} {...campoProps(campo)} />)}
            </div>
        ))}
        {kv?.map((clave, i) => (
            <RevKvRow
                key={i}
                className="rev-skel-form__kv"
                k={<Molde>{clave}</Molde>}
                v={<RevSkeleton kind="texto" />} />
        ))}
        {tips?.map((paso, i) => (
            <RevTipRow
                key={i}
                n={<Molde>{paso.n}</Molde>}
                title={<Molde>{paso.titulo}</Molde>}
                detail={<Molde>{paso.detalle}</Molde>} />
        ))}
        {ayudas?.map((ayuda, i) => {
            const texto = typeof ayuda === "string" ? ayuda : ayuda.texto;
            const conFilete = typeof ayuda === "string" ? false : ayuda.divisor;
            return <Molde key={i} className={cx("rev-help", conFilete && "rev-help--divider")}>{texto}</Molde>;
        })}
        {botones?.map((rotulo, i) => <BotonMolde key={i} fit>{rotulo}</BotonMolde>)}
    </RevCard>
);

const Bloque = ({ bloque }) => {
    if (bloque.card) return <CardMolde {...bloque.card} />;
    if (bloque.alerta) return <AlertaMolde {...bloque.alerta} />;
    if (bloque.danger) return <DangerMolde {...bloque.danger} />;
    return null;
};

/**
 * Forma «form»: la columna de la pantalla con sus bloques, la columna de apoyo y la barra
 * inferior de acciones. Cada bloque lleva UNA clave ('card' | 'alerta' | 'danger').
 */
const Form = ({ bloques = [], aside = [], barra = [], clases = {} }) => (
    <>
        <div className={clases.cols || "rev-cols rev-cols--flush"}>
            <div className={clases.main || "rev-cols__main"}>
                {bloques.map((bloque, i) => <Bloque key={i} bloque={bloque} />)}
            </div>
            {aside.length > 0 &&
                <div className={clases.aside || "rev-cols__aside"}>
                    {aside.map((bloque, i) => <Bloque key={i} bloque={bloque} />)}
                </div>}
        </div>
        {barra.length > 0 &&
            <RevBarra sangre>
                {barra.map((rotulo, i) => <BotonMolde key={i}>{rotulo}</BotonMolde>)}
            </RevBarra>}
    </>
);

const Detalle = ({ cuerpo = "cards", toolbar, miniaturas, filas, celdas, main, aside, clases = {} }) => {
    if (cuerpo === "galeria") {
        return (
            <div className="rev-cols rev-cols--flush">
                <div className="rev-cols__main">
                    <div className="rev-card rev-skel-form__card rev-skel-detalle__filtros">
                        <RevSkeleton kind="line" className="rev-skel-form__titulo" />
                        <RevSkeleton kind="btn" />
                    </div>
                    <Canvas miniaturas={miniaturas} />
                </div>
                <ColumnaAside aside={aside} />
            </div>
        );
    }
    if (cuerpo === "tabla") {
        return (
            <div className="rev-cols rev-cols--main-wide rev-cols--flush">
                <div className="rev-cols__main">
                    {toolbar && (
                        <div className="rev-toolbar rev-skel-detalle__toolbar">
                            <RevSkeleton kind="btn" />
                            <RevSkeleton kind="btn" />
                            <RevSkeleton kind="btn" />
                            <span className="rev-toolbar__actions"><RevSkeleton kind="texto" /></span>
                        </div>
                    )}
                    <Tabla filas={filas} celdas={celdas} clases={clases} />
                </div>
                <ColumnaAside aside={aside} />
            </div>
        );
    }
    return <Cards main={main} aside={aside} clases={clases} />;
};

const Lista = ({ metricas, sinDelta, filas, columnas, clases, etiquetas, contador }) => (
    <>
        <div className={clases.metricas}>
            {Array.from({ length: metricas }, (_, i) => {
                const corta = i >= metricas - sinDelta;
                return (
                    <div key={i} className={cx("rev-metric", "rev-skel-lista__metrica", corta && "rev-skel-lista__metrica--corta")}>
                        <RevSkeleton kind="line" />
                        <RevSkeleton kind="figure" />
                        {!corta && <RevSkeleton kind="line" />}
                    </div>
                );
            })}
        </div>

        {etiquetas?.length > 0 && <div className={clases.filtros}>
            <RevSegmented readOnly items={etiquetas} className="rev-skel rev-skel--molde" aria-hidden="true" />
            <span className={cx(clases.contador, "rev-skel", "rev-skel--molde")} aria-hidden="true"><span>{contador}</span></span>
        </div>}

        <Tabla
            filas={filas}
            celdas={["sub", ...Array(Math.max(columnas - 4, 0)).fill("line"), "chip", "chip", null]}
            clases={clases} />
    </>
);

/**
 * Esqueleto de pantalla completa. `forma="lista"` = métricas + barra de filtros + tabla
 * (artboard L78-108); `forma="detalle"` = el cuerpo de una pestaña de `/events/:id/*`
 * elegido por `cuerpo` ('galeria' = card de filtros + lienzo de `miniaturas`; 'tabla' =
 * barra de herramientas opcional + tabla de `filas`×`celdas`; 'cards' = la composición
 * «form» del artboard, `main` cards y `aside` cards de consejos); `forma="form"` = una
 * pantalla de formulario (Nuevo evento, Perfil, Cobros y venta), con `bloques` en la
 * columna principal, `aside` en la de apoyo y `barra` de rótulos en la barra inferior.
 *
 * La forma `form` no lleva ninguna medida propia: cada pieza es el componente real del DS
 * (`RevCard`, `RevField`, `RevKvRow`, `RevTipRow`, `RevDanger`, `RevAlert`, `RevBarra`)
 * con una barra `texto` dentro de su caja de texto real o, cuando el texto no depende de
 * datos, con la misma cadena traducida como molde. Así el alto de cada fila lo pone el
 * strut real de cada locale.
 *
 * Las clases de la pantalla (rejilla de métricas, superficie, cabecera, fila, contador,
 * columnas) llegan en `clases`: el DS no conoce las clases de `screens/`. `label` es el
 * rótulo i18n de la live region, que tampoco resuelve el DS. `sinDelta` = cuántas de las
 * últimas cards de métrica no llevan línea de variación, y por tanto son más bajas.
 *
 * La barra de filtros no lleva anchos: es un **molde** del segmentado y del contador
 * reales — recibe en `etiquetas` los mismos items que pintaría el estado cargado y en
 * `contador` su misma cadena, y los oculta con `visibility`. Así el punto en el que
 * envuelve lo calcula el navegador con las métricas del texto de cada idioma, en vez de
 * una constante que solo acierta en una locale.
 */
const Page = ({ forma, metricas = 4, sinDelta = 0, filas = 5, columnas = 7, clases = {}, etiquetas, contador, label, className, cuerpo, toolbar, miniaturas, celdas, main, aside, bloques, barra }) => {
    const rotulo = useRotulo(label);
    if (forma === "form") {
        return (
            <div className={cx("rev-skel-form", className)} aria-busy="true">
                <span className="rev-sr-only" role="status">{rotulo}</span>
                <Form bloques={bloques} aside={aside} barra={barra} clases={clases} />
            </div>
        );
    }
    if (forma === "lista") {
        return (
            <div className={cx("rev-skel-lista", className)} aria-busy="true">
                <span className="rev-sr-only" role="status">{rotulo}</span>
                <Lista
                    metricas={metricas}
                    sinDelta={sinDelta}
                    filas={filas}
                    columnas={columnas}
                    clases={clases}
                    etiquetas={etiquetas}
                    contador={contador} />
            </div>
        );
    }
    if (forma === "detalle") {
        return (
            <div className={cx("rev-skel-detalle", className)} aria-busy="true">
                <span className="rev-sr-only" role="status">{rotulo}</span>
                <Detalle
                    cuerpo={cuerpo}
                    toolbar={toolbar}
                    miniaturas={miniaturas}
                    filas={filas}
                    celdas={celdas}
                    main={main}
                    aside={aside}
                    clases={clases} />
            </div>
        );
    }
    return null;
};

/**
 * Hueco de una pestaña o de una card que carga sus propios datos (capa 2): el mismo cuerpo
 * que usa la página, sin cabecera. `tipo` = 'canvas' | 'tabla' | 'filas' | 'barras' |
 * 'cards' | 'imagen'.
 */
const Cuerpo = ({ tipo, label, ...props }) => {
    const rotulo = useRotulo(label);
    return (
        <div className="rev-skel-cuerpo" aria-busy="true">
            <span className="rev-sr-only" role="status">{rotulo}</span>
            {tipo === "imagen" ? <Imagen {...props} />
                : tipo === "canvas" ? <Canvas {...props} />
                    : tipo === "tabla" ? <Tabla {...props} />
                        : tipo === "filas" ? <Filas {...props} />
                            : tipo === "barras" ? <Barras {...props} />
                                : <Cards {...props} />}
        </div>
    );
};

/**
 * Hueco de la cabecera del evento mientras no hay evento: título y línea de meta como
 * barras, y las acciones como **molde** de `RevButton` — reciben en `acciones` los mismos
 * rótulos traducidos que pintaría el estado cargado, ocultos con `visibility`, así que
 * envuelven donde envuelve el real en cada idioma.
 */
const Cabecera = ({ acciones = [] }) => (
    <RevPageHead
        className="rev-pagehead--tabs"
        aria-busy="true"
        title={<RevSkeleton kind="title" className="rev-skel-detalle__titulo" />}
        meta={[0, 1, 2, 3].map((i) => (
            <span key={i} className="rev-skel-detalle__meta"><RevSkeleton kind="texto" /></span>
        ))}
        actions={acciones.map((rotulo, i) => (
            <RevButton key={i} as="span" className="rev-skel rev-skel--molde" aria-hidden="true">
                <span>{rotulo}</span>
            </RevButton>
        ))}
    />
);

RevSkeleton.Page = Page;
RevSkeleton.Cuerpo = Cuerpo;
RevSkeleton.Cabecera = Cabecera;
RevSkeleton.Boton = BotonMolde;

export default RevSkeleton;
